import React, {useEffect, useMemo} from "react"
// MUI
import {Box, Stack, Typography, Select, MenuItem, FormControl} from "@mui/material"
import {alpha} from "@mui/material/styles"
import {
    GridViewRounded,
    SettingsRounded,
    QrCode2Rounded,
    SellRounded,
    DownloadRounded,
    MonitorHeartRounded,
    DriveFolderUploadRounded,
    PhoneAndroidRounded,
    InsightsRounded,
    Inventory2Rounded,
    GroupsRounded,
    KeyboardArrowDownRounded,
} from "@mui/icons-material"
// Context
import {useUser} from "@/context/UserProvider"
import {useModule} from "@/context/ModuleProvider"
import {isMainOrgManager} from "@/utils/orgAccess"
import {useData} from "@/context/PackagingDataProvider"
// Translation
import {useTranslation} from "react-i18next"
// Types & tokens
import {PackagingSection} from "./types"
import {packagingTokens as tk} from "@/theme/packagingTokens"

type SidebarProps = {
    section: PackagingSection
    onSectionChange: (section: PackagingSection) => void
    organisationFilter: string
    setOrganisationFilter: (value: string) => void
    // Extra sx merged onto the <aside> (used to show/hide it per breakpoint).
    sx?: object
}

type NavEntry = {
    id: PackagingSection
    label: string
    icon: React.ReactElement<any>
    badge?: { text: string; tone?: "primary" | "purple" }
    show?: boolean
    // Rendered but not clickable (greyed, with `hint` as the tooltip) — for
    // sections the user may know about but not open.
    disabled?: boolean
    hint?: string
    // CSS anchor-name so the onboarding tour can point at this rail item.
    anchorName?: string
}

// ─── A single rail nav item — the canonical Rémi sidebar button ───
function NavItem(props: { entry: NavEntry; active: boolean; onClick: () => void }) {
    const {entry, active, onClick} = props
    return (
        <Box
            component={"button"}
            type={"button"}
            id={`pkg-nav-${entry.id}`}
            onClick={entry.disabled ? undefined : onClick}
            disabled={entry.disabled}
            aria-disabled={entry.disabled || undefined}
            title={entry.disabled ? entry.hint : undefined}
            aria-current={active ? "page" : undefined}
            sx={(theme) => ({
                position: "relative",
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                px: 1.5,
                py: 1.1,
                anchorName: entry.anchorName,
                border: "none",
                borderRadius: tk.radius.sm,
                cursor: entry.disabled ? "not-allowed" : "pointer",
                opacity: entry.disabled ? 0.45 : 1,
                fontFamily: "inherit",
                fontSize: "13.5px",
                fontWeight: active ? 600 : 500,
                textAlign: "left",
                color: active ? theme.palette.primary.main : tk.color.textSecondary,
                backgroundColor: active ? alpha(theme.palette.primary.main, 0.1) : "transparent",
                transition: "all .15s ease",
                "& svg.nav-icon": {
                    width: 18,
                    height: 18,
                    flexShrink: 0,
                    opacity: active ? 1 : 0.6,
                },
                "&:hover": active || entry.disabled
                    ? {}
                    : {
                          backgroundColor: alpha(theme.palette.primary.main, 0.06),
                          color: tk.color.textPrimary,
                      },
                "&::before": active
                    ? {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          top: "50%",
                          transform: "translateY(-50%)",
                          width: 3,
                          height: 20,
                          backgroundColor: theme.palette.primary.main,
                          borderRadius: "0 3px 3px 0",
                      }
                    : undefined,
            })}
        >
            {React.cloneElement(entry.icon, {className: "nav-icon"})}
            <span style={{flex: 1}}>{entry.label}</span>
            {entry.badge && (
                <Box
                    component={"span"}
                    sx={(theme) => ({
                        fontSize: "11px",
                        fontWeight: 600,
                        px: "7px",
                        py: "2px",
                        borderRadius: "10px",
                        color:
                            entry.badge!.tone === "purple"
                                ? tk.color.purple
                                : theme.palette.primary.main,
                        backgroundColor:
                            entry.badge!.tone === "purple"
                                ? tk.color.purple100
                                : alpha(theme.palette.primary.main, 0.12),
                    })}
                >
                    {entry.badge.text}
                </Box>
            )}
        </Box>
    )
}

function SectionLabel({children}: {children: React.ReactNode}) {
    return (
        <Typography
            sx={{
                fontSize: "10.5px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: tk.color.textTertiary,
                px: 1.5,
                pt: 2,
                pb: 0.75,
            }}
        >
            {children}
        </Typography>
    )
}

export default function PackagingSidebar(props: SidebarProps) {
    const {section, onSectionChange, organisationFilter, setOrganisationFilter, sx} = props

    const {t} = useTranslation(["common"])
    const {
        user, getUser,
        fullOrganisation, getFullOrganisation,
        userRoles,
    } = useUser()
    const {modules} = useModule()
    const {products, totalProducts, brands, ranges, apps} = useData()

    useEffect(() => {
        if (!user) getUser()
        if (!fullOrganisation) getFullOrganisation()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Build the organisation list (root org + visible sub-orgs; members only
    // see their own org) so the rail selector lists real organisations.
    const orgItems = useMemo(() => {
        const items: {id: string; name: string}[] = []
        if (!!fullOrganisation && fullOrganisation.is_root_org) {
            items.push({
                id: fullOrganisation.organisation_id,
                name: fullOrganisation.organisation_name,
            })
            if (!!user && user.organisation_role !== userRoles.member) {
                const subs = fullOrganisation.sub_organisations
                if (!!subs) {
                    subs.forEach((sub: any) =>
                        items.push({id: sub.organisation_id, name: sub.organisation_name}),
                    )
                }
            }
        }
        return items
    }, [fullOrganisation, user, userRoles])

    const productsCount = totalProducts ?? (products ? products.length : undefined)

    // Healthchecks are an account-wide tool: only OWNER/ADMIN of the main
    // organisation may open the tab. Everyone else sees it greyed out.
    const canUseHealthchecks = isMainOrgManager(user, fullOrganisation)

    // ─── Grouped navigation model ───
    const groups: {label: string; items: NavEntry[]}[] = [
        {
            label: t("nav_overview"),
            items: [
                {id: "dashboard", label: t("nav_dashboard"), icon: <GridViewRounded />},
            ],
        },
        {
            label: t("nav_manage"),
            items: [
                {
                    id: "products",
                    label: t("products"),
                    icon: <QrCode2Rounded />,
                    badge:
                        productsCount !== undefined
                            ? {text: String(productsCount)}
                            : undefined,
                },
                {
                    id: "brands",
                    label: t("brands_ranges"),
                    icon: <SellRounded />,
                    // Mirrors the label's duality: brands count · ranges count.
                    badge:
                        brands && ranges
                            ? {text: `${brands.length} · ${ranges.length}`}
                            : undefined,
                },
                {
                    id: "apps",
                    label: t("apps"),
                    icon: <PhoneAndroidRounded />,
                    show: modules.geoloc,
                    badge: apps ? {text: String(apps.length)} : undefined,
                },
            ],
        },
        {
            label: t("nav_tools"),
            items: [
                {
                    id: "batches",
                    label: t("nav_batch_management"),
                    icon: <DriveFolderUploadRounded />,
                },
                {
                    id: "download",
                    label: t("nav_batch_download"),
                    icon: <DownloadRounded />,
                    anchorName: "--dl",
                },
                {
                    id: "health",
                    label: t("nav_healthcheck"),
                    icon: <MonitorHeartRounded />,
                    anchorName: "--hc",
                    disabled: !canUseHealthchecks,
                    hint: t("healthchecks_main_org_only"),
                },
            ],
        },
        {
            label: t("nav_insights"),
            items: [
                {
                    id: "activity",
                    label: t("nav_team_activity"),
                    icon: <InsightsRounded />,
                },
            ],
        },
        {
            label: t("nav_configuration"),
            items: [
                {
                    id: "settings",
                    label: t("nav_settings"),
                    icon: <SettingsRounded />,
                },
            ],
        },
    ]

    return (
        <Box
            component={"aside"}
            sx={{
                width: tk.layout.sidebarWidth,
                flexShrink: 0,
                alignSelf: "stretch",
                display: "flex",
                flexDirection: "column",
                backgroundColor: tk.color.surface,
                borderRight: `1px solid ${tk.color.border}`,
                ...sx,
            }}
        >
            {/* App label */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.75,
                    py: 1.75,
                    borderBottom: `1px solid ${tk.color.borderLight}`,
                }}
            >
                <Box
                    sx={(theme) => ({
                        width: 28,
                        height: 28,
                        borderRadius: tk.radius.sm,
                        backgroundColor: alpha(theme.palette.primary.main, 0.12),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: theme.palette.primary.main,
                    })}
                >
                    <Inventory2Rounded sx={{fontSize: 16}} />
                </Box>
                <Box>
                    <Typography sx={{fontWeight: 700, fontSize: "14px", lineHeight: 1.2}}>
                        Packaging
                    </Typography>
                    <Typography sx={{fontSize: "11px", color: tk.color.textTertiary}}>
                        {t("application_menu")}
                    </Typography>
                </Box>
            </Box>

            {/* Organisation selector */}
            <Box id={"pkg-org-select"} sx={{px: 1.75, py: 1.5, borderBottom: `1px solid ${tk.color.borderLight}`}}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.625,
                        mb: 0.75,
                        color: tk.color.textTertiary,
                    }}
                >
                    <GroupsRounded sx={{fontSize: 14}} />
                    <Typography
                        sx={{
                            fontSize: "10.5px",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                        }}
                    >
                        {t("organisation")}
                    </Typography>
                </Box>
                <FormControl fullWidth size={"small"}>
                    <Select
                        value={organisationFilter}
                        displayEmpty
                        onChange={(e) => setOrganisationFilter(e.target.value as string)}
                        IconComponent={KeyboardArrowDownRounded}
                        sx={{
                            backgroundColor: tk.color.bg,
                            fontSize: "13px",
                            fontWeight: 600,
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: tk.color.border,
                            },
                            "& .MuiSelect-select": {py: "8px"},
                        }}
                    >
                        <MenuItem value={""} sx={{fontSize: "13px"}}>
                            {t("all_organisations")}
                        </MenuItem>
                        {orgItems.map((org) => (
                            <MenuItem key={org.id} value={org.id} sx={{fontSize: "13px"}}>
                                {org.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Navigation */}
            <Box component={"nav"} sx={{flex: 1, overflowY: "auto", px: 1.5, py: 1}}>
                {groups.map((group) => {
                    const visible = group.items.filter((it) => it.show !== false)
                    if (!visible.length) return null
                    return (
                        <React.Fragment key={group.label}>
                            <SectionLabel>{group.label}</SectionLabel>
                            <Stack spacing={0.25}>
                                {visible.map((entry) => (
                                    <NavItem
                                        key={entry.id}
                                        entry={entry}
                                        active={section === entry.id}
                                        onClick={() => onSectionChange(entry.id)}
                                    />
                                ))}
                            </Stack>
                        </React.Fragment>
                    )
                })}
            </Box>
        </Box>
    )
}
